import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';
import { resolveChannel, channelMeta, displayTicketId } from '../utils/channels';
import { XCircleIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import ReviewItemModal from '../components/ReviewItemModal';

export default function Queue() {
  const [queueItems, setQueueItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  // Delete (spam/junk) — soft delete with an audit reason.
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState('spam');
  const [deleteNote, setDeleteNote] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchQueue();
  }, []);

  // Auto-refresh: poll every 45s while the tab is visible, and refetch the
  // moment the user returns to the tab. Silent (no toast/spinner) and paused
  // while a modal/dialog is open so it never disrupts an in-progress review.
  useEffect(() => {
    const REFRESH_MS = 45000;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (selectedItem || deletingItem) return;  // don't disrupt an open modal
      fetchQueue(true);
    };
    const id = setInterval(tick, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [selectedItem, deletingItem]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedItems = useMemo(() => {
    const sorted = [...queueItems];
    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.key) {
        case 'created_at':
          aVal = new Date(a.ticket?.created_at || 0);
          bVal = new Date(b.ticket?.created_at || 0);
          break;
        case 'priority':
          const priorityOrder = { urgent: 0, standard: 1, low: 2 };
          aVal = priorityOrder[a.priority] ?? 3;
          bVal = priorityOrder[b.priority] ?? 3;
          break;
        case 'ticket':
          aVal = a.ticket?.freshdesk_ticket_id || '';
          bVal = b.ticket?.freshdesk_ticket_id || '';
          break;
        case 'customer':
          aVal = (a.ticket?.customer_name || a.ticket?.customer_email || '').toLowerCase();
          bVal = (b.ticket?.customer_name || b.ticket?.customer_email || '').toLowerCase();
          break;
        case 'subject':
          aVal = (a.ticket?.subject || '').toLowerCase();
          bVal = (b.ticket?.subject || '').toLowerCase();
          break;
        case 'intent':
          aVal = (a.classification?.intent || '').toLowerCase();
          bVal = (b.classification?.intent || '').toLowerCase();
          break;
        case 'confidence':
          aVal = a.classification?.confidence || 0;
          bVal = b.classification?.confidence || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [queueItems, sortConfig]);

  // Client-side search over the loaded queue — match by customer name, email,
  // or ticket/phone number (the wa: prefix is ignored so a plain phone matches).
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedItems;
    const digits = q.replace(/\D/g, '');
    return sortedItems.filter((item) => {
      const t = item.ticket || {};
      const ref = (t.freshdesk_ticket_id || '').replace(/^wa:/, '').toLowerCase();
      const haystack = [t.customer_name, t.customer_email, ref, t.subject]
        .filter(Boolean).join(' ').toLowerCase();
      if (haystack.includes(q)) return true;
      // Number match: compare digits-only so 0788… matches a stored 2778…
      if (digits.length >= 4) {
        const refDigits = ref.replace(/\D/g, '');
        if (refDigits.includes(digits) || refDigits.includes(digits.replace(/^0/, ''))) return true;
      }
      return false;
    });
  }, [sortedItems, searchQuery]);

  const SortHeader = ({ label, sortKey, className = '' }) => (
    <th
      className={`px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-50 hover:bg-gray-100 select-none ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="flex flex-col">
          <ChevronUpIcon className={`h-3 w-3 ${sortConfig.key === sortKey && sortConfig.direction === 'asc' ? 'text-primary-600' : 'text-gray-300'}`} />
          <ChevronDownIcon className={`h-3 w-3 -mt-1 ${sortConfig.key === sortKey && sortConfig.direction === 'desc' ? 'text-primary-600' : 'text-gray-300'}`} />
        </span>
      </div>
    </th>
  );

  const fetchQueue = async (silent = false) => {
    const toastId = silent ? null : toast.loading('Refreshing queue...');
    try {
      const response = await apiClient.get('/api/v1/queue/');
      setQueueItems(response.data);
      if (!silent) toast.success('Queue refreshed successfully', { id: toastId });
    } catch (error) {
      console.error('Error fetching queue:', error);
      if (!silent) toast.error('Failed to refresh queue', { id: toastId });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Open the delete confirmation for a queue item (from the row or the modal).
  const openDelete = (item) => {
    setDeletingItem(item);
    setDeleteCategory('spam');
    setDeleteNote('');
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    const toastId = toast.loading('Deleting...');
    try {
      await apiClient.post(`/api/v1/queue/${deletingItem.id}/delete`, {
        category: deleteCategory,
        reason: deleteNote,
      });
      toast.success('Deleted (kept in audit history)', { id: toastId });
      // Drop it from the visible list immediately, then refresh.
      setQueueItems((items) => items.filter((i) => i.id !== deletingItem.id));
      if (selectedItem && selectedItem.id === deletingItem.id) setSelectedItem(null);
      setDeletingItem(null);
      fetchQueue();
    } catch (error) {
      console.error('Error deleting item:', error);
      const msg = error?.response?.status === 403
        ? 'You do not have permission to delete'
        : 'Failed to delete item';
      toast.error(msg, { id: toastId });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading queue...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Review Queue</h1>
          <p className="mt-2 text-sm text-gray-600">
            {searchQuery.trim()
              ? `${filteredItems.length} of ${queueItems.length} items`
              : `${queueItems.length} items awaiting review`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, email, or number…"
              className="w-64 pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <XCircleIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            onClick={fetchQueue}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Queue Table — own scroll area so rows always have a scroller, with
          the header pinned. */}
      <div className="bg-white shadow rounded-lg overflow-auto max-h-[70vh]">
        <table className="w-full min-w-[720px] divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <SortHeader label="Date" sortKey="created_at" className="w-[10%]" />
              <SortHeader label="Priority" sortKey="priority" className="w-[10%]" />
              <SortHeader label="Ticket" sortKey="ticket" className="w-[8%]" />
              <SortHeader label="Customer" sortKey="customer" className="w-[18%]" />
              <SortHeader label="Subject" sortKey="subject" className="w-[22%]" />
              <SortHeader label="Intent" sortKey="intent" className="w-[12%]" />
              <SortHeader label="Conf" sortKey="confidence" className="w-[10%]" />
              <th className="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-2 py-12 text-center text-gray-500">
                  {searchQuery.trim() ? 'No items match your search' : 'No items in queue'}
                </td>
              </tr>
            ) : (
              filteredItems.map((item) => {
                const dateStr = item.ticket?.created_at || item.queued_at;
                const date = dateStr ? new Date(dateStr) : null;
                const isValidDate = date && !isNaN(date.getTime());

                return (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-2 py-3 text-xs text-gray-500">
                    {isValidDate ? (
                      <div className="whitespace-nowrap">
                        {date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    ) : (
                      <div className="text-gray-400">-</div>
                    )}
                  </td>
                  <td className="px-2 py-3">
                    <span
                      className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.priority === 'urgent'
                          ? 'bg-red-100 text-red-800'
                          : item.priority === 'standard'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {item.priority}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-xs text-gray-500">
                    {(() => {
                      const meta = channelMeta(resolveChannel(item.ticket));
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span
                            className={`inline-flex items-center w-fit px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.classes}`}
                            title={meta.label}
                          >
                            {meta.label}
                          </span>
                          <span className="truncate" title={item.ticket?.freshdesk_ticket_id}>
                            #{displayTicketId(item.ticket?.freshdesk_ticket_id)}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-3">
                    <div className="text-xs font-medium text-gray-900 truncate" title={item.ticket?.customer_name || 'Unknown'}>
                      {item.ticket?.customer_name || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-400 truncate" title={item.ticket?.customer_email}>
                      {item.ticket?.customer_email}
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <div className="text-xs text-gray-900 truncate" title={item.ticket?.subject}>
                      {item.ticket?.subject}
                    </div>
                    {item.summary && (
                      <div className="text-xs text-indigo-700 truncate mt-0.5" title={item.summary}>
                        {item.summary}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-3">
                    <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                      {item.classification?.intent?.replace('_', ' ') || 'N/A'}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-sm text-gray-900">
                    {item.classification?.confidence
                      ? `${item.classification.confidence.toFixed(0)}%`
                      : 'N/A'}
                  </td>
                  <td className="px-2 py-3 text-sm font-medium whitespace-nowrap">
                    <button
                      onClick={() => setSelectedItem(item)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      View
                    </button>
                    <button
                      onClick={() => openDelete(item)}
                      className="ml-3 text-red-600 hover:text-red-900"
                      title="Delete (spam/junk) — kept in audit history"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal — shared with the "Escalated to person" queue */}
      <ReviewItemModal
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onActioned={fetchQueue}
        onRequestDelete={openDelete}
      />

      {/* Delete confirmation (soft delete + audit reason) */}
      {deletingItem && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900">Delete this message?</h3>
            <p className="mt-1 text-sm text-gray-600">
              It will be removed from the queue but kept in audit history — your name,
              the time, and the reason are recorded. The conversation is not deleted.
            </p>

            <label className="block mt-4 text-sm font-medium text-gray-700">Reason</label>
            <select
              value={deleteCategory}
              onChange={(e) => setDeleteCategory(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="spam">Spam</option>
              <option value="duplicate">Duplicate</option>
              <option value="test_invalid">Test / Invalid</option>
              <option value="other">Other</option>
            </select>

            <label className="block mt-3 text-sm font-medium text-gray-700">Note (optional)</label>
            <textarea
              value={deleteNote}
              onChange={(e) => setDeleteNote(e.target.value)}
              rows={2}
              placeholder="Add any detail for the audit log…"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setDeletingItem(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
